import React, { useState, useEffect, useRef, useCallback } from 'react';
import TiptapEditor from './TiptapEditor';
import { TEMPLATE_VARIABLES } from '../../config/templateVariables';
import Toast from '../Common/Toast';
import UnsavedChangesPopover from '../Common/UnsavedChangesPopover';
import './Documents.css';

const DocumentEditor = ({
  document,
  onSave,
  onCancel,
  templateName,
  substituteVariables,
  onExportWord,
  noOverlay = false // новый параметр для контроля отображения overlay
}) => {
  const [formData, setFormData] = useState({
    name: document?.name || document?.title || '',
    content: document?.content || '',
    header_content: document?.header_content || '',
    footer_content: document?.footer_content || ''
  });
  const [editorValue, setEditorValue] = useState(document?.content || '');

  const [saving, setSaving] = useState(false);
  const [isFullscreen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [toast, setToast] = useState(null);

  const initialFormData = useRef(JSON.stringify(formData));
  const formDataRef = useRef(formData);
  const contentRef = useRef(formData.content || '');
  const editorRef = useRef(null);
  const hasChangesRef = useRef(false);

  const buildSnapshot = useCallback((data, content) => JSON.stringify({
    name: data?.name || '',
    content: content ?? '',
    header_content: data?.header_content || '',
    footer_content: data?.footer_content || '',
  }), []);

  const updateChangeState = useCallback((data, content) => {
    const currentFormData = buildSnapshot(data, content);
    const hasDataChanged = currentFormData !== initialFormData.current;
    setHasChanges(hasDataChanged);
    hasChangesRef.current = hasDataChanged;
  }, [buildSnapshot]);

  useEffect(() => {
    const sourceName = document?.name || document?.title || '';
    const sourceContent = String(document?.content || '');
    const sourceHeader = String(document?.header_content || '');
    const sourceFooter = String(document?.footer_content || '');
    const nextContent = (sourceContent && substituteVariables)
      ? substituteVariables(sourceContent)
      : sourceContent;

    const nextFormData = {
      name: sourceName,
      content: nextContent,
      header_content: sourceHeader,
      footer_content: sourceFooter,
    };
    setFormData(nextFormData);
    setEditorValue(nextContent);
    contentRef.current = nextContent;
    formDataRef.current = nextFormData;
    initialFormData.current = buildSnapshot(nextFormData, nextContent);
    hasChangesRef.current = false;
    setHasChanges(false);
  }, [
    document?.id,
    document?.name,
    document?.title,
    document?.content,
    document?.header_content,
    document?.footer_content,
    buildSnapshot,
  ]);

  useEffect(() => {
    formDataRef.current = formData;
    updateChangeState(formData, contentRef.current);
  }, [formData, updateChangeState]);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const handleContentChange = useCallback((content) => {
    contentRef.current = content;
    updateChangeState(formDataRef.current, content);
  }, [updateChangeState]);

  const handleEditorDirty = useCallback(() => {
    setHasChanges(true);
    hasChangesRef.current = true;
  }, []);

  const getCurrentEditorContent = useCallback(() => {
    const content = editorRef.current?.getContentWithMargins?.() || contentRef.current || '';
    contentRef.current = content;
    return content;
  }, []);

  const handleCancel = useCallback(() => {
    if (hasChangesRef.current) {
      setShowConfirmDialog(true);
    } else {
      onCancel();
    }
  }, [onCancel]);

  const handleConfirmClose = useCallback(() => {
    setShowConfirmDialog(false);
    onCancel();
  }, [onCancel]);

  // Обработчик клавиши Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // Важно: внутри карточки торга документ открывается поверх AuctionModal.
        // AuctionModal тоже слушает Escape на document, поэтому дополнительно
        // блокируем обработчики ниже по цепочке, чтобы не закрывалась карточка торга.
        e.stopImmediatePropagation?.();

        // Если открыт диалог подтверждения выхода без сохранения,
        // повторный Esc должен просто закрывать этот диалог.
        if (showConfirmDialog) {
          setShowConfirmDialog(false);
          return;
        }

        // Приоритет: сначала закрываем любой видимый toast (локальный или родительский).
        const hasVisibleToast = !!window.document?.querySelector?.('.toast');
        if (hasVisibleToast) {
          try {
            window.document.querySelectorAll('.toast').forEach((el) => {
              if (el && el.parentNode) {
                el.parentNode.removeChild(el);
              }
            });
          } catch (_) {}
          window.dispatchEvent(new Event('aot-toast-dismiss'));
          if (toast && toast.message) {
            setToast(null);
          }
          return;
        }

        // Если не в полноэкранном режиме, закрываем редактор
        if (!isFullscreen) {
          handleCancel();
        }
      }
    };

    if (typeof window !== 'undefined' && window.document) {
      // Ставим capture=true, чтобы перехватить Escape раньше родительских модалок
      // (например, AuctionModal) и не допустить закрытия нескольких окон разом.
      window.document.addEventListener('keydown', handleEscape, true);
      return () => {
        window.document.removeEventListener('keydown', handleEscape, true);
      };
    }
  }, [isFullscreen, handleCancel, showConfirmDialog, toast]);

  const handleSave = async ({ exit = false } = {}) => {
    if (saving) {
      return false;
    }
    setSaving(true);
    try {
      // Проверяем токены перед сохранением
      const tokens = localStorage.getItem('tokens');
      console.log('Document save - tokens check:', tokens ? 'present' : 'missing');

      if (!tokens) {
        console.error('No tokens found during save attempt');
        alert('Сессия истекла. Пожалуйста, войдите в систему заново.');
        return false;
      }

      const dataToSave = {
        title: formData.name,
        content: getCurrentEditorContent()
      };

      console.log('Attempting to save document with data:', dataToSave);
      await onSave(document?.id, dataToSave, { exit });

      // Обновляем начальное состояние после успешного сохранения
      initialFormData.current = buildSnapshot(formDataRef.current, contentRef.current);
      hasChangesRef.current = false;
      setHasChanges(false);
      if (!exit) {
        showToast('Изменения сохранены', 'success');
      }
      return dataToSave;
    } catch (error) {
      console.error('Document save error:', error);
      if (error.response?.status === 401) {
        alert('Ошибка аутентификации. Пожалуйста, войдите в систему заново.');
      } else {
        alert('Ошибка сохранения: ' + (error.response?.data?.detail || error.message));
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndExit = async () => {
    const result = await handleSave({ exit: true });
    if (result !== false) {
      onCancel();
    }
  };

  const handleExportWord = () => {
    if (!onExportWord) return;
    onExportWord({
      title: formData.name,
      content: getCurrentEditorContent(),
      header_content: formData.header_content,
      footer_content: formData.footer_content,
    });
  };

  const content = (
    <div className={`template-editor-modal document-editor-compact ${isFullscreen ? 'fullscreen' : ''}`}>
        <div className="editor-header">
          <div className="document-editor-meta-line">
            {templateName && (
              <div className="document-editor-meta-template">
                <span>Шаблон:</span>
                <strong>{templateName}</strong>
              </div>
            )}
            <label className="document-editor-title-inline">
              <span>Название документа:</span>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="form-control"
                placeholder="Введите название документа"
              />
            </label>
          </div>
        </div>

        <div className="editor-body">
          <div className="editor-content">
            <TiptapEditor
              ref={editorRef}
              value={editorValue}
              onChange={handleContentChange}
              onDirty={handleEditorDirty}
              placeholder="Содержимое документа..."
              showVariables={false}
              showConditions={false}
              performanceMode
              availableVariables={TEMPLATE_VARIABLES}
            />
          </div>
        </div>

        <div className="editor-footer">
          <div className="footer-left">
            {hasChanges && (
              <span className="unsaved-indicator">
                ● Есть несохраненные изменения
              </span>
            )}
          </div>

          <div className="footer-actions">
            {typeof onExportWord === 'function' && (
              <button
                className="btn btn-info"
                onClick={handleExportWord}
                disabled={saving || !formData.content?.trim()}
              >
                Экспорт в Word (.docx)
              </button>
            )}
            <button
              className="btn btn-success"
              onClick={() => handleSave({ exit: false })}
              disabled={saving || !formData.name.trim()}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSaveAndExit}
              disabled={saving || !formData.name.trim()}
            >
              {saving ? 'Сохранение...' : 'Сохранить и выйти'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={saving}
            >
              Отмена
            </button>
          </div>
        </div>
      </div>
  );

  const overlays = (
    <>
      <UnsavedChangesPopover
        isOpen={showConfirmDialog}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmClose}
        zIndex={12000}
      />
      <Toast toast={toast} />
    </>
  );

  // Если noOverlay = true, возвращаем контент без обертки
  if (noOverlay) {
    return (
      <>
        {content}
        {overlays}
      </>
    );
  }

  // Иначе возвращаем с modal-overlay
  return (
    <div className={`modal-overlay ${isFullscreen ? 'fullscreen' : ''}`}>
      {content}
      {overlays}
    </div>
  );
};

export default DocumentEditor;
